<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="salesAreas"
      :headers="salesAreasHeaders"
      :list="salesAreas"
      @add-new="onAddNewSalesArea"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="loadData"
      table-id="sales-areas"
      clickable
      @table-item-clicked="onEditSalesArea"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as SalesArea)" />
        </div>
      </template>
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <SalesAreasModalCreateEdit
      v-if="createEditModalActive"
      :sales-area="salesArea"
      :sales-areas="salesAreas"
      :mode="createEditModalMode"
      :title="modalEditCreateTitle()"
      @close-modal="createEditModalActive = false"
      @save="onSave"></SalesAreasModalCreateEdit>
    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';

import { getSalesAreas, removeSalesAreasByIds } from '@/services/api/user';
import { filterActive, filterData, sortData } from '@/utils/table';
import SalesAreasModalCreateEdit from '@/components/routes/SalesAreasModalCreateEdit.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import type { TableFilterData } from '@/types/Base';
import type { SalesArea } from '@/types/SalesAreas';

const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();

const salesAreas = ref<SalesArea[]>([]);
const filterIsActive = ref<boolean | undefined>();

const salesAreasHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  await loadData();
});

const salesArea = ref<SalesArea>(initializeSalesArea());

const createEditModalActive = ref(false);

const createEditModalMode = ref<'add' | 'edit'>('add');

function initializeSalesArea(): SalesArea {
  return {
    id: uuidv4(),
    name: '',
    code: '',
    isActive: true,
    isFromManager: true,
  };
}

function onAddNewSalesArea() {
  salesArea.value = initializeSalesArea();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onEditSalesArea(item: SalesArea) {
  salesArea.value = item;
  createEditModalActive.value = true;
  createEditModalMode.value = 'edit';
}

function onSave(modifiedData: SalesArea) {
  if (!salesAreas.value) return;

  const index = salesAreas.value.findIndex((item) => item.id === modifiedData.id);
  if (index !== -1) salesAreas.value[index] = modifiedData;
  else salesAreas.value.push(modifiedData);
  createEditModalActive.value = false;
}

async function loadData(data?: TableFilterData) {
  let response = await getSalesAreas();
  if (!response) return;

  if (data && data.query) {
    response = filterData(response, data.query, ['name', 'code']);
  }

  if (data && data.column !== undefined && data.order) {
    response = sortData(response, data.column, data.order, salesAreasHeaders);
  }

  if (data && data.isActive !== undefined) response = filterActive(response, data.isActive);

  salesAreas.value = response;
}

async function onDeleteMultiselect(items: SalesArea[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  if (!idsToDelete.length) {
    toastStore.warning({ text: $t('messages.info.selectedItemsCannotBeDeleted') });
    return;
  }
  const res = await removeSalesAreasByIds(idsToDelete);
  if (!res && salesAreas.value)
    salesAreas.value = salesAreas.value.filter((type) => !idsToDelete.includes(type.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeSalesAreasByIds([idToDelete]);
  if (!res && salesAreas.value)
    salesAreas.value = salesAreas.value.filter((type) => type.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<SalesArea>();
function onDeleteRow(item: SalesArea) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function modalEditCreateTitle() {
  if (salesArea.value && !salesArea.value.isFromManager)
    return `${$t('common.actions.view')}  ${$t('salesAreas.salesArea')} `;
  return createEditModalMode.value === 'edit'
    ? `${$t('common.actions.edit')} ${$t('salesAreas.salesArea')}`
    : `${$t('common.actions.create')} ${$t('salesAreas.salesArea')}`;
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  loadData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  loadData({});
}
</script>
